import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import worldMap from '@highcharts/map-collection/custom/world.geo.json';

import { Graphic, Period } from '../../../hooks/types';
import { Charts } from '../../ui';
import { GraphicTitle, GraphicWrapper } from '../style';

export type UpdateAnalyticsProps = {
  date: Period;
  loginProject: string | null;
  country: string | null;
};

type Props = {
  countries: Graphic | null;
  error: boolean;
  rangeTitle: string;
};

export const getState = (body: Graphic | null, error: boolean): 'fail' | 'empty' | 'normal' => {
  if (error) {
    return 'fail';
  }
  if (!body || body.points.length === 0) {
    return 'empty';
  }
  return 'normal';
};

const CountriesGraphic = ({ countries, rangeTitle, error }: Props): JSX.Element => {
  const intl = useIntl();

  const translatedData = useMemo(
    () =>
      countries &&
      countries.points
        .filter((point) => point.key !== 'OTHER')
        .map((point) => ({
          ...point,
          name: intl.formatMessage({ id: `form.select.country-name.${point.key}` })
        })),
    [countries]
  );

  const toLocaleString = useCallback(
    (value: string | number) => intl.formatNumber(Number(value)),
    [intl]
  );

  return (
    <GraphicWrapper>
      <GraphicTitle data-test-id="analytics-page__user-countries__title">
        {intl.formatMessage({ id: 'analytics.charts.countries' })}
      </GraphicTitle>
      <Charts
        name="user-countries"
        state={getState(countries, error)}
        options={{
          chart: {
            width: null
          },
          tooltip: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            header: rangeTitle,
            valueFormatter: toLocaleString
          },
          colorAxis: {
            min: 0,
            tickInterval: 0,
            tickAmount: 2,
            minColor: '#C1DEFF',
            maxColor: '#0060CE'
          },
          series: [
            {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              data: translatedData,
              name: '',
              joinBy: ['iso-a2', 'key'],
              mapData: worldMap
            }
          ]
        }}
        constructorType="mapChart"
      />
    </GraphicWrapper>
  );
};

export default CountriesGraphic;
